/**
 * Creates a feature flag with multiple activation methods:
 * 1. Environment variable (REACT_APP_ENABLE_{FLAG_NAME}=true) - global enable
 * 2. Query param (?test_{flagName}) - enables for session, persists in sessionStorage
 * 3. Optional default check function (e.g., date range)
 *
 * @param {string} flagName - Unique identifier for the feature (e.g., "connected_apps").
 * @param {Function} [defaultCheck] - Optional function for default state (e.g., date range).
 * @returns {Function} A function that returns true if the feature is enabled.
 *
 * @example
 * // Simple flag
 * const isFeatureXEnabled = createFeatureFlag( "feature_x" );
 * // Enabled by: REACT_APP_ENABLE_FEATURE_X=true OR ?test_feature_x in URL
 *
 * // Flag with date-based default
 * const isPromoActive = createFeatureFlag( "promo", () => {
 *     const now = new Date();
 *     return now >= START_DATE && now <= END_DATE;
 * } );
 */
export const createFeatureFlag = ( flagName, defaultCheck = () => false ) => {
	// Convert flag name to env var format: "my_flag" -> "REACT_APP_ENABLE_MY_FLAG"
	const envVarName = `REACT_APP_ENABLE_${ flagName.toUpperCase() }`;
	const queryParamName = `test_${ flagName }`;

	return () => {
		// 1. Check environment variable (global enable)
		if ( process.env[ envVarName ] === "true" ) {
			return true;
		}

		// 2. Check query param / sessionStorage (testing override)
		if ( typeof window !== "undefined" && typeof sessionStorage !== "undefined" ) {
			const urlParams = new URLSearchParams( window.location.search );
			if ( urlParams.has( queryParamName ) ) {
				sessionStorage.setItem( queryParamName, "true" );
			}
			if ( sessionStorage.getItem( queryParamName ) === "true" ) {
				return true;
			}
		}

		// 3. Fall back to default check
		return defaultCheck();
	};
};
